import { ChatBubble, Close, CopyAll, Print, Share, Sync } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import ErrorIcon from '@mui/icons-material/Error'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import { Alert, Backdrop, Box, Button, CardMedia, CircularProgress, darken, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Fab, FormControl, getContrastRatio, Grid, lighten, LinearProgress, Snackbar, TextField, Typography } from '@mui/material'
import { grey } from '@mui/material/colors'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { API_BASE, dpsf, spinAnim } from '../../app/const'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { fireauth, getUserRolesAsync, selectAuthUser, selectPdfApiVersion } from '../auth/AuthSlice'
import ChatConvWindow from '../chat/ChatConv'
import { setCustomize, setIsPreview } from '../customize/CustomizeSlice'
import CompMap from '../utils/CompMap'
import CompTable from '../utils/CompTable'
import FeedbackBtn from '../utils/FeedbackBtn'
import GeneratedOn from '../utils/GeneratedOn'
import { GOOGLE_MAPS_API_KEY } from '../utils/GoogleMapsService'
import Graphy from '../utils/Grphy'
import IconBox2 from '../utils/IconBox2'
import SemiCircularProgress from '../utils/SemiCircularProgress'
import AnalysisPrint from './AnalysisPrint'
import { clearSlug, fetchAnalysisDataForSlugAsync, fetchMarketingMaterialForSlugAsync, selectAnalysisData, selectAnalysisFault, selectAnalysisStatus, selectChatGenesis, selectDomain, selectDomainCss, selectMarketingMaterial, selectMarketingMaterialStatus, setDomain } from './AnalysisSlice'

let debounceTimerAnalysis: any
const useQuery = () => new URLSearchParams(useLocation().search)

export interface AnalysisProps {
  slugOrAddress?: string
}
function Analysis(props: AnalysisProps) {
  let { slugOrAddress } = useParams()
  if (props.slugOrAddress && !slugOrAddress) slugOrAddress = props.slugOrAddress
  const dispatch = useAppDispatch()
  const [compCount, setCompCount] = useState<number>(25)
  const [propertyType, setPropertyType] = useState<string>(`Single Family`)
  const [copied, setCopied] = useState(false)
  const [showMap, setShowMap] = useState(true)
  const [showMore, setShowMore] = useState(false)
  const [faultShown, setFaultShown] = useState(false)
  const [copyVal, SetCopyVal] = useState()
  const [showChatBubble, setShowChatBubble] = useState(false)
  const [printableMap, setPrintableMap] = useState(false)
  const [stikyBarTop, setStikyBarTop] = useState<any>(undefined)
  const [showError, setShowError] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const analysisStatus = useAppSelector(selectAnalysisStatus)
  const marketingMaterialStatus = useAppSelector(selectMarketingMaterialStatus)
  const analysisData = useAppSelector(selectAnalysisData)
  const analysisFault = useAppSelector(selectAnalysisFault)
  const chatGenesis = useAppSelector(selectChatGenesis)
  const marketingMaterial = useAppSelector(selectMarketingMaterial)
  const pdfApiVersion = useAppSelector(selectPdfApiVersion)
  const navigate = useNavigate()
  const location = useLocation()
  const user = useAppSelector(selectAuthUser)
  const query = useQuery()
  const [beds, setBeds] = useState(query.get('beds'))
  const [baths, setBaths] = useState(query.get('baths'))
  const [sqft, setSqft] = useState(query.get('sqft'))
  const [advanced, setAdvanced] = useState<boolean>((beds || baths || sqft) ? true : false)
  const domain = useAppSelector(selectDomain)
  const domainCss = useAppSelector(selectDomainCss)
  const [mapImgB64, setMapImgB64] = useState<string>('')
  const [graphAvgImgB64, setAvgGraphImgB64] = useState<string>('')
  const [graphTotImgB64, setTotGraphImgB64] = useState<string>('')
  const [clickedExport, setClickedExport] = useState(false)
  // https://developers.google.com/maps/documentation/maps-static/start#Markers
  const mapImg = (address: string) => `https://maps.googleapis.com/maps/api/staticmap?size=600x400&key=${GOOGLE_MAPS_API_KEY}&center=${address}&markers=color:orange%7C${address}`

  const mapStreetImg = (address: string) => `https://maps.googleapis.com/maps/api/streetview?size=600x400&v=3.59&location=${address}&key=${GOOGLE_MAPS_API_KEY}`

  const isSticky = (e: any) => {
    const sidebarEl = document.querySelector('.mystikybar')
    const scrollTop = window.scrollY
    if (scrollTop >= stikyBarTop - 250) {
      sidebarEl?.classList.add('mystickyclass')
    } else {
      sidebarEl?.classList.remove('mystickyclass')
    }
  }

  const fetchAnalysisDataAsyncDebounced = () => {
    if (slugOrAddress) {
      console.log(`slugOrAddress: ${slugOrAddress}, compCount: ${compCount}, propertyType: ${propertyType}`)
      if (slugOrAddress.indexOf('error') === 0 || slugOrAddress === 'CREDIT_EXHAUSTED') {
        console.log(`showing error, because slugOrAddress: ${slugOrAddress}`)
        setShowError(true)
      } else {
        const domain = query?.get('domain')
        dispatch(setDomain(domain ? domain : undefined))
        dispatch(fetchAnalysisDataForSlugAsync({ slug: slugOrAddress, domain: domain ? domain : undefined }))
      }
    }
  }

  useEffect(() => {
    clearTimeout(debounceTimerAnalysis)
    debounceTimerAnalysis = setTimeout(fetchAnalysisDataAsyncDebounced, 500)
  }, [compCount, slugOrAddress, dispatch, propertyType])

  useEffect(() => {
    const sidebarEl = document.querySelector('.mystikybar')?.getBoundingClientRect()
    if (sidebarEl) {
      setStikyBarTop(sidebarEl.top)
    }
    return () => {
      dispatch(clearSlug())
    }
  }, [])

  useEffect(() => {
    if (!stikyBarTop) return
    window.addEventListener('scroll', isSticky)
    return () => {
      window.removeEventListener('scroll', isSticky)
    }
  }, [stikyBarTop])

  const isPreview = query?.get('preview') === 'true'
  const primaryColor = query?.get('primaryColor') ?? ''
  const secondaryColor = query?.get('secondaryColor') ?? ''
  const tertiaryColor = query?.get('tertiaryColor') ?? ''
  const logoB64 = query?.get('logoB64') ?? ''
  const logoObjFit = query?.get('logoObjFit') ?? ''
  const logoWidth = Number(query?.get('logoWidth') ?? 200)
  const logoHeight = Number(query?.get('logoHeight') ?? 50)

  useEffect(() => {
    if (isPreview) {
      dispatch(setIsPreview(true))
      dispatch(setCustomize({
        primaryColor,
        secondaryColor,
        tertiaryColor,
        logoB64,
        logoObjFit,
        logoWidth,
        logoHeight,
      }))
    }
    console.log(`isPreview: ${isPreview}, primaryColor: ${primaryColor}, secondaryColor: ${secondaryColor}, tertiaryColor: ${tertiaryColor}, logoObjFit: ${logoObjFit}, logoWidth: ${logoWidth}, logoHeight: ${logoHeight}, logoB64: ${logoB64}`)
  }, [isPreview, primaryColor, secondaryColor, tertiaryColor, logoB64, logoObjFit, logoWidth, logoHeight])

  const handleCloseCopied = () => { setCopied(false) }
  const handleCloseFault = () => { setFaultShown(true) }
  const handleCloseErrorMsg = () => {
    setShowError(false)
    navigate(`/home`)
  }

  useEffect(() => {
    if (clickedExport) {
      setTimeout(() => { setClickedExport(false) }, 15000)
    }
  }, [clickedExport])

  return (<Box>
    <Backdrop
      sx={{ backgroundColor: () => analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.85) : domainCss?.backdropBG, zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={analysisStatus === 'loading'}>
      <CircularProgress />
    </Backdrop>
    <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={copied} autoHideDuration={6000} onClose={handleCloseCopied}>
      <Alert icon={<CheckIcon fontSize="inherit" />} onClose={handleCloseCopied} sx={{ width: '100%' }}>
        Copied!
      </Alert>
    </Snackbar>
    {!domain && <Grid container spacing={2} className='print-hide' >
      <Grid item xs={12} sx={{ textAlign: 'left', paddingTop: '0 !important' }}>
        <Button variant='text' size='small' onClick={() => {
          dispatch(getUserRolesAsync({})) // to update the credit count on the home page
          navigate(`/home`)
        }}> <NavigateBeforeIcon /> New Estimate
        </Button>
      </Grid>
    </Grid>}
    {showError && <Box sx={{ margin: 4 }}> <Alert icon={<ErrorIcon fontSize="inherit" />} severity='error'
      onClose={handleCloseErrorMsg} sx={{ width: '100%', marginBottom: 2 }}>
      An error has occurred. Please try again later. If the issue persists, contact our support team for assistance. {slugOrAddress}
    </Alert>
      {slugOrAddress === 'CREDIT_EXHAUSTED' ? <Button variant='contained' color='primary' sx={{ mardingTop: 40, textTransform: 'none', marginLeft: 2 }} onClick={() => { navigate(`/subscription/price`) }}>
        Buy Credits
      </Button>
        : <Button variant='contained' color='error' sx={{ mardingTop: 40, textTransform: 'none' }} onClick={handleCloseErrorMsg}>
          Close
        </Button>}
    </Box>}
    {analysisData?.detail ? <>
      <Grid id="analysis-wrap" container spacing={2} mb={4} className='print-hide'>
        <Grid item xs={12} md={9} sx={{ paddingLeft: 0 }}>
          <Typography variant="h4" component="div" sx={{ textAlign: 'left', paddingLeft: 0 }}>
            <strong>{analysisData?.detail?.formattedAddress}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={3} sx={{ textAlign: 'left', display: 'flex', justifyContent: 'end' }}>
          <CopyToClipboard onCopy={() => setCopied(true)} text={`${window.location.origin}/analysis/slug/${analysisData?.slug}${domain ? '?domain=' + domain
            : user?.email?.toLowerCase().endsWith('@purepm.co') ? '?domain=pure'
              : user?.email?.toLowerCase().endsWith('@theparisgroupnw.com') ? '?domain=theParisGroupNw'
                : user?.email?.toLowerCase().endsWith('@traversepm.com') ? '?domain=traversepm'
                  : user?.email?.toLowerCase().endsWith('@gtlrealestate.com') ? '?domain=gtlrealestate'
                    : ''}`}>
            <Button variant='contained' size='small' color="primary" sx={{
              textTransform: 'none', fontSize: 12,
              height: 36, width: '125px', backgroundColor: analysisData?.customize?.primaryColor ?? domainCss?.buttonBG,
              color: getContrastRatio(analysisData?.customize?.primaryColor ?? domainCss?.buttonBG, domainCss?.buttonText) < 4.5 ? '#000000' : '#ffffff',
              '&:hover': { backgroundColor: darken(domainCss?.buttonBG, 0.5), color: getContrastRatio(darken(domainCss?.buttonBG, 0.5), domainCss?.buttonText) < 4.5 ? '#000000' : '#ffffff' }
            }}>
              <Share sx={{ fontSize: 12, mr: 1 }} /> Share
            </Button>
          </CopyToClipboard>
          <Button disabled={clickedExport || mapImgB64.length <= 0} sx={{
            ml: 2, textTransform: 'none', fontSize: 12, height: 36,
            width: '125px', backgroundColor: analysisData?.customize?.primaryColor ?? domainCss?.buttonBG,
            color: getContrastRatio(analysisData?.customize?.primaryColor ?? domainCss?.buttonBG, domainCss?.buttonText) < 4.5 ? '#000000' : '#ffffff',
            '&:hover': { backgroundColor: darken(domainCss?.buttonBG, 0.5), color: getContrastRatio(darken(domainCss?.buttonBG, 0.5), domainCss?.buttonText) < 4.5 ? '#000000' : '#ffffff' }
          }} variant='contained' size='small' onClick={async () => {
            /*if (pdfApiVersion === 'v2' || pdfApiVersion === '') {
              window.location.href = `${PDF_BASE}api/pdf2/${analysisData?.slug}`
              setClickedExport(true)
            } else {
            setPrintableMap(true)
            window.print()
            setPrintableMap(false)
            }*/
            try {
              const pdfRsp = await axios.get(`${API_BASE}apps/estimate/${slugOrAddress}/pdf`, {
                headers: {
                  "Content-Type": "application/json; charset=UTF-8",
                  "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
                }
              })
              //window.open(((pdfRsp.data as any).body as any).pdfUrl, '', "popup")
              window.location.href = ((pdfRsp.data as any).body as any).pdfUrl
            } catch (e) {
              console.log('Pdf failed', e)
              setPrintableMap(true)
              window.print()
              setPrintableMap(false)
            }
          }}>{clickedExport ?
            <Sync sx={{ fontSize: 12, mr: 1, animation: `${spinAnim} 1s linear infinite` }} />
            : <Print sx={{ fontSize: 12, mr: 1 }} />} Export PDF
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2} mb={4} className='print-hide'>
        <Grid item xs={12} md={9} sx={{ borderRadius: 1, border: 1, borderColor: grey[300] }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
              <Typography variant="h2" component="div" sx={{ textAlign: 'center' }}>
                <strong>${isFinite(analysisData?.comp) ? parseInt(analysisData?.comp, 10).toLocaleString() : analysisData?.comp}</strong>
              </Typography>
              <Box sx={{ backgroundColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.85) : domainCss?.backdropBG, borderRadius: 1, mb: 2, padding: 1, border: 1, borderColor: grey[300] }}>
                <Typography variant='caption' component='div' sx={{ textAlign: 'center', color: grey[700], mb: 1 }}>
                  Confidence score
                </Typography>
                {analysisData?.confidence && <SemiCircularProgress value={analysisData?.confidence} />}
              </Box>
              <FeedbackBtn variant='outlined' sx={{ width: '100%', mb: 1, textTransform: 'none' }} />
            </Grid>
            <Grid item xs={12} md={8} sx={{ border: 0, paddingRight: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Typography variant="h5" component="div" sx={{ textAlign: 'left' }} ml={1}>
                <strong>Rentfinder Analysis</strong>
              </Typography>
              <Typography variant="caption" component="div" m={1} sx={{ textAlign: 'justify' }}>
                {analysisData?.description}
              </Typography>
              <Grid container spacing={0} sx={{
                borderRadius: 1, marginTop: 1, marginBottom: 1, backgroundColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.85) : domainCss?.backdropBG
              }}>
                <Grid item xs sx={{ padding: 0, borderRight: 2, borderColor: '#FFFFFF' }}>
                  <Typography variant="h6" component="div" sx={{ pt: 1, textAlign: 'center' }}>
                    {analysisData.comps?.listings?.length}
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ pb: 1, textAlign: 'center' }}>
                    Comps
                  </Typography>
                </Grid>

                {analysisData?.detail?.squareFootage && analysisData?.comp && parseInt(analysisData?.comp, 10) ? <Grid item xs={6} md={4} sx={{ padding: 0, borderRight: 2, borderColor: '#FFFFFF' }}>
                  <Typography variant="h6" component="div" sx={{ pt: 1, textAlign: 'center' }}>
                    {dpsf(parseInt(analysisData?.comp, 10), analysisData?.detail?.squareFootage)}
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ pb: 1, textAlign: 'center' }}>
                    $/sqft
                  </Typography>
                </Grid> : <></>}

                <Grid item xs sx={{ padding: 0 }}>
                  <Typography variant="h6" component="div" sx={{ pt: 1, textAlign: 'center' }}>
                    {analysisData.comps?.listings?.filter((e: any) => e.correlation > 0.98)?.length}
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ pb: 1, textAlign: 'center' }}>
                    98% Similar
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', pt: 0 }}>
              <Typography variant="caption" component="span" sx={{ textAlign: 'left', color: domainCss?.instructionColor }}>
                Powered by
              </Typography>
              <img src="/images/v2/logo-menubar2.png" width="150" />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={3} sx={{ paddingTop: '0 !important', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          {analysisData.zDetail?.hiResImageLink && analysisData.zDetail?.hiResImageLink.indexOf('https://maps.googleapis.com/maps') !== 0 ? <CardMedia sx={{ borderRadius: 1, mb: 2 }}
            component="img" width="100%" image={analysisData.zDetail?.hiResImageLink} alt="Property image" />
            : <CardMedia sx={{ borderRadius: 1, mb: 2 }}
              component="img" width="100%" image={mapStreetImg(analysisData?.detail?.formattedAddress)} alt="Google Street view image" />}
          {<CardMedia onClick={() => {
            //!domain && navigate(`${location.pathname}/map`) 
          }} sx={{ borderRadius: 1, cursor: 'pointer' }}
            component="img" width="100%" image={mapImg(analysisData?.detail?.formattedAddress)}
            alt="Maps"
          />}
        </Grid>
      </Grid>
      <Grid container spacing={2} mb={4} sx={{ border: 1, borderRadius: 1, borderColor: grey[300], backgroundColor: analysisData?.customize?.secondaryColor ? lighten(analysisData?.customize?.secondaryColor, 0.85) : domainCss?.backdropBG, pb: 2 }} className='print-hide'>
        <Grid item xs={6} md={3}>
          <IconBox2 width="50%" icon="/images/bedroom.svg" title="Bedrooms:" subtitle={analysisData?.detail?.bedrooms} />
        </Grid>
        <Grid item xs={6} md={3}>
          <IconBox2 icon="/images/bathroom.svg" title="Bathrooms:" subtitle={analysisData?.detail?.bathrooms} />
        </Grid>
        <Grid item xs={6} md={3}>
          <IconBox2 width="50%" icon="/images/sqft.svg" title="Square Feet:" subtitle={`${analysisData?.detail?.squareFootage} ft²`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <IconBox2 width="50%" icon="/images/type.svg" title="Property Type:" subtitle={analysisData?.detail?.propertyType} showEdit={true} onEdit={() => { setShowEdit(true) }} />
        </Grid>
      </Grid>
    </> : <> <Snackbar open={!!analysisFault && !showError} autoHideDuration={6000} onClose={handleCloseFault}>
      <Alert icon={<ErrorIcon fontSize="inherit" />} severity='error'
        onClose={handleCloseFault} sx={{ width: '100%' }}>
        An error occurred, please try again later. {analysisFault}
      </Alert>
    </Snackbar> </>
    }



    {!showError && <>

      {!domain && <Grid container spacing={2} mb={4} className='print-hide'>
        <Grid item xs={12} sx={{ paddingLeft: '0 !important' }}>
          <Typography variant='h5' sx={{ textAlign: 'left', mb: 1 }}>Marketing material</Typography>
        </Grid>
        <Grid item xs={12} sx={{ borderRadius: 1, border: 1, borderColor: grey[300], padding: 2 }}>
          <Grid container spacing={2} sx={{ borderBottom: marketingMaterial?.short_marketing_description ? 1 : 0, borderColor: grey[300] }}>
            <Grid item xs={12} md={8}>
              <Typography variant="caption" component="div" m={1} sx={{ textAlign: 'justify', color: domainCss?.instructionColor }}>
                Marketing material can be useful in various places. Use Rentfinder™ marketing material and get an edge over other listings to attract tenants, highlight the property's features. It can also be used in various marketing channels, such as websites, social media, and printed materials.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', paddingRight: 2 }}>
              {slugOrAddress && <Button variant='outlined' disabled={marketingMaterialStatus === 'loading'}
                onClick={() => slugOrAddress && dispatch(fetchMarketingMaterialForSlugAsync({ slug: slugOrAddress }))}
                sx={{ mb: 1, textTransform: 'none' }} >
                Generate
              </Button>}
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ border: 0, paddingRight: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', marginTop: marketingMaterial?.short_marketing_description ? 2 : 0 }}>
              {marketingMaterialStatus === 'loading' && <Box sx={{ textAlign: 'center' }}>
                <LinearProgress color="secondary" />
              </Box>}
              {marketingMaterial && <>
                <Typography variant="h5" component="div" sx={{ textAlign: 'left' }} ml={1}>
                  <strong>Short description</strong>
                </Typography>
                <Typography variant="caption" component="div" m={1} sx={{ textAlign: 'justify' }}>
                  {marketingMaterial?.short_marketing_description}
                  <CopyToClipboard onCopy={() => setCopied(true)} text={marketingMaterial?.short_marketing_description}>
                    <Button size='small' variant='text'><CopyAll /></Button>
                  </CopyToClipboard>
                </Typography>
                <Typography variant="h5" component="div" sx={{ textAlign: 'left' }} ml={1}>
                  <strong>Long description</strong>
                </Typography>
                <Typography variant="caption" component="div" m={1} sx={{ textAlign: 'justify' }}>
                  {marketingMaterial?.long_marketing_description}
                  <CopyToClipboard onCopy={() => setCopied(true)} text={marketingMaterial?.long_marketing_description}>
                    <Button size='small' variant='text'><CopyAll /></Button>
                  </CopyToClipboard>
                </Typography>
              </>}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={3} sx={{ paddingTop: '0 !important', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}></Grid>
      </Grid>}

      <Grid container spacing={2} mb={4} sx={{ borderRadius: 1, borderColor: grey[300], pb: 2 }} className='print-hide'>
        <Grid item xs={12} sx={{ paddingLeft: '0 !important' }}>
          <Typography variant='h5' sx={{ textAlign: 'left', mb: 1 }}>Rental Comps</Typography>
          {analysisData?.comps?.listings && <CompTable />}
        </Grid>
      </Grid>
      <Grid container spacing={2} mb={4} sx={{ pb: 2 }} className='print-hide'>
        <Grid item xs={12} sx={{ padding: '0 !important' }}>
          {analysisData?.comps?.listings && showMap && <CompMap printable={true} showMap={showMap} onMapCompleted={(image) => setMapImgB64(image)} />}
        </Grid>
      </Grid>
    </>}

    {
      analysisData?.comps?.listings && showChatBubble && <ChatConvWindow history={analysisData?.comps?.listings}
        chatGenesis={chatGenesis} slug={analysisData?.slug}
        onRegenerate={(slug: string) => { dispatch(fetchAnalysisDataForSlugAsync({ slug, domain: domain ? domain : undefined })) }}
        onClose={() => { setShowChatBubble(!showChatBubble) }} className='print-hide' />
    }

    <AnalysisPrint data={analysisData} map={mapImg(analysisData?.detail?.formattedAddress)} mapImgB64={mapImgB64} graphAvgImgB64={graphAvgImgB64} graphTotImgB64={graphTotImgB64} />

    {!showError && <>
      <Grid container spacing={2} mb={4} sx={{ pb: 2 }} className='print-hide'>
        <Grid item xs={12} sx={{ paddingLeft: '0 !important' }}>
          <Graphy onRenderCompleted={(image, type) => type === 'average' ? setAvgGraphImgB64(image) : setTotGraphImgB64(image)} />
        </Grid>
      </Grid>

      <Typography component="div" variant="caption" sx={{ marginTop: 12, textAlign: 'justify', color: domainCss?.instructionColor }}>
        Rentfinder™ estimates are for informational purposes only and are based on the assumption that the subject property is in average condition and features standard amenities. Factors such as property condition, amenities/upgrades/incentives and furnishings offered, preparation/cleaning time between tenants, marketing channels utilized to find tenants, and leasing seasonality can significantly impact the vacancy term and, in turn, the accuracy of the Rentfinder™ estimates. The rental data is derived from limited data sets that may be updated or changed at our sole discretion. We are under no obligation to maintain the currency of the rental data, nor do we offer any warranties, expressed or implied, concerning the completeness, accuracy, reliability, suitability, or availability of such rental data or the underlying data sets. The rental data does not and is not intended to provide financial, investment, tax, or legal advice. <strong>Utilizing or relying upon the</strong> Rentfinder™ estimates <strong>is solely at your own risk.</strong> WE MAKE NO REPRESENTATIONS, EXCEPT AS SET FORTH HEREIN, OR WARRANTIES WHATSOEVER RELATING TO THE Rentfinder™ estimates, INCLUDING WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE.  The Rentfinder™ estimates are provided in an “AS IS”, “WHERE IS”, and “WITH ALL FAULTS” basis. Copyright 2023 Rentfinder™, unauthorized use, duplication, redistribution, or disclosure is strictly prohibited by law.
      </Typography>
    </>}
    <GeneratedOn url={window.location.href} />

    {
      chatGenesis && !domain && <Fab color="primary" aria-label="upload picture" sx={[{
        margin: 0, top: 'auto', bottom: 75, left: 'auto', position: 'fixed',
      }
        , showChatBubble && { right: -100, opacity: 0 }
        , !showChatBubble && { right: 50, opacity: 0.7 }]}
        onClick={() => { setShowChatBubble(!showChatBubble) }} className='print-hide'>
        {showChatBubble ? <Close /> : <ChatBubble />}
      </Fab>
    }

    <Dialog
      open={showEdit}
      onClose={() => setShowEdit(false)}>
      <DialogTitle>Modify Bed/Bath/Sqft</DialogTitle>
      <DialogContent>
        <DialogContentText gutterBottom>
          Update information and hit <i>Regenerate report</i>, your credits will <strong>not</strong> be used.
        </DialogContentText>
        <Box sx={{ width: '100%', textAlign: 'left' }}>
          <FormControl sx={{ width: '100%', textAlign: 'center' }}>
            <FormControl sx={{ m: 1 }} variant="outlined">
              <TextField id="customize-beds" label="Beds" variant="outlined" defaultValue={beds || analysisData?.detail?.bedrooms}
                onChange={(event) => setBeds(event.target.value)} />
            </FormControl>
            <FormControl sx={{ m: 1 }} variant="outlined">
              <TextField id="customize-baths" label="Baths" variant="outlined" defaultValue={baths || analysisData?.detail?.bathrooms}
                onChange={(event) => setBaths(event.target.value)} />
            </FormControl>
            <FormControl sx={{ m: 1 }} variant="outlined">
              <TextField id="customize-sqft" label="Sq.ft." variant="outlined" defaultValue={sqft || analysisData?.detail?.squareFootage}
                onChange={(event) => setSqft(event.target.value)} />
            </FormControl>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowEdit(false)}>Cancel</Button>
        <Button onClick={() => {
          dispatch(clearSlug())
          navigate(`/analysis/addr/${analysisData?.slug}?beds=${beds || analysisData?.detail?.bedrooms}&baths=${baths || analysisData?.detail?.bathrooms}&sqft=${sqft || analysisData?.detail?.squareFootage}`)
        }}>Regenerate report</Button>
      </DialogActions>
    </Dialog>
  </Box>
  )
}

export default Analysis
